import * as React from 'react';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';

export interface DataListColumn<T> {
  key: string;
  label: string;
  render: (item: T) => React.ReactNode;
  className?: string;
}

export interface DataListAction<T> {
  label: string;
  icon?: React.ReactNode;
  onClick?: (item: T) => void;
  href?: (item: T) => string;
  variant?: 'default' | 'destructive' | 'ghost';
  canShow?: (item: T) => boolean;
  title?: string;
}

interface DataListCardProps<T> {
  items: T[];
  columns: DataListColumn<T>[];
  actions?: DataListAction<T>[];
  keyExtractor: (item: T) => string | number;
  isLoading?: boolean;
  loadingMessage?: string;
  emptyMessage?: string;
  error?: string | null;
}

export function DataListCard<T>({
  items,
  columns,
  actions = [],
  keyExtractor,
  isLoading = false,
  loadingMessage = 'Loading...',
  emptyMessage = 'No items found.',
  error = null,
}: DataListCardProps<T>) {
  if (isLoading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#D71F20] mx-auto" />
        <p className="mt-3 text-[#84878A] text-sm">{loadingMessage}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-sm text-red-600">{error}</p>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="text-center py-8 text-[#84878A]">
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {items.map((item) => (
        <div
          key={keyExtractor(item)}
          className="bg-[#F2F2F2] p-3 rounded-lg flex items-center justify-between"
        >
          <div
            className="grid gap-4 flex-1"
            style={{ gridTemplateColumns: `repeat(${columns.length}, minmax(0, 1fr))` }}
          >
            {columns.map((column) => (
              <div key={column.key} className={column.className}>
                <span className="text-xs text-[#84878A]">{column.label}</span>
                <div className="text-sm font-medium">{column.render(item)}</div>
              </div>
            ))}
          </div>

          {actions.length > 0 && (
            <div className="flex items-center gap-1 ml-4">
              {actions.map((action, index) => {
                const shouldShow = action.canShow ? action.canShow(item) : true;
                if (!shouldShow) return null;

                if (action.href) {
                  return (
                    <Link
                      key={index}
                      to={action.href(item)}
                      title={action.title}
                      className="inline-flex h-8 cursor-pointer items-center justify-center rounded-md px-3 text-sm font-semibold text-[#0A0A9E] transition-colors hover:bg-white"
                    >
                      {action.icon}
                      {action.label && <span className={action.icon ? 'ml-1' : ''}>{action.label}</span>}
                    </Link>
                  );
                }

                return (
                  <Button
                    key={index}
                    variant={action.variant || 'ghost'}
                    size="sm"
                    onClick={() => action.onClick?.(item)}
                    title={action.title}
                    className="h-8 px-2 hover:bg-white"
                  >
                    {action.icon}
                    {action.label && <span className={action.icon ? 'ml-1' : ''}>{action.label}</span>}
                  </Button>
                );
              })}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
